import {
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { setCredentials, logout } from "../../features/auth/authSlice";
import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { toast } from "react-hot-toast";
import { urlConfig } from "../../config";
import { Mutex } from "async-mutex";

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: `${urlConfig.baseUrl}`,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const refreshQuery = fetchBaseQuery({
  baseUrl: `${urlConfig.baseUrl}`,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.refreshToken;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 403) {
    const errorMessage = (result?.error?.data as any)?.message?.toLowerCase() || '';
    // Permission errors (like KYC not verified, category access) should NOT logout
    // Session error is "Access Denied" (exact match) - should logout
    const isPermissionError =
      errorMessage.includes('kyc') ||
      errorMessage.includes('category') ||
      errorMessage.includes('permission');

    if (!isPermissionError) {
      // Session-related 403 errors (e.g., "Access Denied") should logout
      toast.error("Session Expired or Unauthorized");
      api.dispatch(logout());
    }
    // Permission errors are handled by individual components
  }

  if (result?.error?.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      if ((result?.error?.data as any)?.message?.includes("OTP required")) {
        release();
        return result;
      }

      try {
        console.log("Sending refresh token");

        //send refresh token to get a new access token
        const refreshResult: any = await refreshQuery(
          `${urlConfig.authBaseUrl}/auth/refresh`,
          api,
          extraOptions
        );

        console.log("Refresh result", refreshResult);

        if (refreshResult?.data) {
          const user = api.getState().auth.user;
          api.dispatch(
            setCredentials({
              user,
              token: refreshResult.data?.token,
              refreshToken: refreshResult.data?.refreshToken,
            })
          );

          //retry the initial request
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      //wait for the mutex to unlock and retry the initial request
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({}),
  tagTypes: [
    "Categories",
    "Cards",
    "Coins",
    "Orders",
    "Banks",
    "Withdraws",
    "Wallet",
    "BankList",
    "Kyc",
    "DepositAddress",
    "Session",
    "TopSellers",
    "TopDepositors",
    "LeaderboardBoard",
    "LeaderboardRewards",
    "RateAlerts",
    "Referrals",
    "ReferredUsers",
    "ReferralRewards",
    "Ebills",
    "Streak",
    "Giftcards",
    "TreasuryChest",
    "CryptoPayouts",
    "CryptoPayoutDetail",
    "MomoWithdrawals",
  ],
});
